import React, { useState, useRef, useCallback, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { db, storage } from './App';
import { useShop } from './App';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { scanImage, decodeVin, chatTurn, generateTechSummary } from './lib/geminiScan';

const STATE_DISCLAIMERS = {
  'CA': "CALIFORNIA AUTOMOTIVE REPAIR ACT AUTHORIZATION\n\nI hereby authorize the repair work hereinafter set forth to be done along with the necessary material and agree that you are not responsible for loss or damage to vehicle or articles left in vehicle in case of fire, theft or any other cause beyond your control or for any delays caused by unavailability of parts or delays in parts shipments by the supplier or transporter. \n\nI hereby grant you and/or your employees permission to operate the vehicle herein described on streets, highways or elsewhere for the purpose of testing and/or inspection. \n\nAn express mechanic's lien is hereby acknowledged on above vehicle to secure the amount of repairs thereto. I acknowledge receipt of a copy of this form.\n\nNOTICE TO CUSTOMER: A WRITTEN ESTIMATE FOR REPAIRS MUST BE PROVIDED TO THE CUSTOMER BEFORE ANY WORK IS DONE. THE SHOP MAY NOT CHARGE MORE THAN THE ESTIMATE WITHOUT THE CUSTOMER'S CONSENT.",
  
  'FL': "FLORIDA MOTOR VEHICLE REPAIR ACT AUTHORIZATION\n\nI request a written estimate for repairs to be performed. I understand that under state law the motor vehicle repair shop must provide a written estimate of repairs costing more than $100 and may not charge more than 10 percent above the estimate without my consent.\n\nI authorize the repair work and grant permission to operate the vehicle for testing and inspection. I acknowledge the shop's lien rights under Florida Statutes for unpaid repairs.",
  
  'NY': "NEW YORK REPAIR SHOP REGISTRATION ACT\n\nI authorize the repair work set forth. I understand that I am entitled to a written estimate if the repair cost is expected to exceed $50. I grant permission to operate the vehicle for testing. I acknowledge the shop's lien for the cost of repairs.",
  
  'TX': "TEXAS NOTICE TO CUSTOMERS & AUTHORIZATION\n\nI hereby authorize the repair work hereinafter set forth to be done along with the necessary material. I grant you and/or your employees permission to operate the vehicle for testing and/or inspection. \n\nNOTICE: POSSESSION OF THIS VEHICLE WILL NOT BE SURRENDERED PRIOR TO PAYMENT FOR REPAIRS AND CHARGES. An express mechanic's lien is hereby acknowledged on the above vehicle to secure the amount of repairs thereto. \n\nSTORAGE: Storage charges will accrue beginning on the 3rd day after notification that repairs are complete.",

  'IL': "ILLINOIS AUTOMOTIVE REPAIR ACT WAIVER\n\nI understand that I have the right to a written estimate if the repairs will exceed $100. By signing below, I waive my right to a written estimate and authorize the shop to proceed with repairs up to the dollar amount verbally agreed upon. \n\nI authorize the repair work and grant permission to operate the vehicle for testing. I acknowledge the shop's lien rights for unpaid repairs.",

  'DEFAULT': "CHAIN OF CUSTODY & SERVICE AUTHORIZATION\n\nBy signing below, I authorize the repair facility (\"Shop\") to take possession and custody of the vehicle described herein. I understand this is an authorization for custody and diagnosis/service, NOT a final financial authorization for repairs.\n\n1. CHAIN OF CUSTODY - I grant the Shop permission to operate, drive, and test my vehicle as necessary for diagnosis and repair. The Shop is authorized to take possession of the vehicle for these purposes.\n\n2. NO FINANCIAL OBLIGATION YET - This document establishes the Shop's custody of the vehicle. A separate written estimate with specific dollar amounts will be provided for your approval before any billable repair work begins, as required by law.\n\n3. MECHANIC'S LIEN NOTICE - The Shop has a legal right to retain possession of the vehicle until all agreed-upon charges are paid in full, pursuant to applicable state mechanic's lien statutes.\n\n4. STORAGE CHARGES - If vehicle is not retrieved within 3 business days after completion notice, storage charges apply at the posted daily rate.\n\n5. LIMITED LIABILITY - The Shop is not responsible for: pre-existing conditions, personal property left in vehicle, or consequential damages.\n\n6. TCPA CONSENT - I consent to receive calls, texts (SMS/MMS), and emails regarding my vehicle and services. I may opt out at any time.\n\n7. ELECTRONIC SIGNATURE - My electronic signature acknowledges transfer of custody and has the same legal force as a handwritten signature.\n\nBY SIGNING, I CERTIFY THAT I AM THE LEGAL OWNER OR AUTHORIZED REPRESENTATIVE AND GRANT CUSTODY OF THE VEHICLE TO THE SHOP."
};

const getDisclaimer = (shop) => {
  const state = shop?.address?.state || 'DEFAULT';
  // Check for specific state match, otherwise use DEFAULT
  return STATE_DISCLAIMERS[state] || STATE_DISCLAIMERS['DEFAULT'];
};

const getIPAddress = async () => {
  try {
    const r = await fetch('https://api.ipify.org?format=json');
    return (await r.json()).ip;
  } catch (e) {
    return 'Unable to capture';
  }
};

const getDeviceInfo = () => {
  const ua = navigator.userAgent;
  let browser = 'Unknown';
  let os = navigator.platform;
  if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
  else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
  else if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Edg')) browser = 'Edge';
  if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('Mac')) os = 'macOS';
  else if (ua.includes('Windows')) os = 'Windows';
  return {
    userAgent: ua,
    platform: navigator.platform,
    browser: browser,
    os: os,
    simplified: browser + ' on ' + os,
    language: navigator.language,
    screenResolution: window.screen.width + 'x' + window.screen.height,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  };
};

const generateTransactionId = () => {
  return 'AUTH-' + Date.now().toString(36).toUpperCase() + '-' + Math.random().toString(36).substring(2, 8).toUpperCase();
};

// Static vehicle make list — defined outside component so hooks see a stable reference
const MAKES = ['Acura', 'Audi', 'BMW', 'Buick', 'Cadillac', 'Chevrolet', 'Chrysler', 'Dodge', 'Ford', 'GMC', 'Honda', 'Hyundai', 'Infiniti', 'Jeep', 'Kia', 'Lexus', 'Lincoln', 'Mazda', 'Mercedes-Benz', 'Mitsubishi', 'Nissan', 'Porsche', 'Ram', 'Subaru', 'Tesla', 'Toyota', 'Volkswagen', 'Volvo', 'Other'];

const formatPhone = (v) => {
  const d = v.replace(/\D/g, '');
  if (d.length >= 6) {
    return '(' + d.slice(0, 3) + ') ' + d.slice(3, 6) + '-' + d.slice(6, 10);
  } else if (d.length >= 3) {
    return '(' + d.slice(0, 3) + ') ' + d.slice(3);
  }
  return d;
};

const IntakeForm = () => {
  const { shop, shopId, loading: shopLoading, error: shopError } = useShop();
  const advisorName = shop?.advisorName?.trim() || 'Kenneth';
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    firstName: '', lastName: '', phone: '', email: '',
    streetAddress: '', city: '', state: '', zipCode: '',
    vehicleYear: '', vehicleMake: '', vehicleModel: '', vehicleEngine: '',
    licensePlate: '', vin: '', reasonForVisit: '', additionalNotes: ''
  });
  const [platePhoto, setPlatePhoto] = useState(null);
  const [platePreview, setPlatePreview] = useState(null);
  const [vinPhoto, setVinPhoto] = useState(null);
  const [vinPreview, setVinPreview] = useState(null);
  const [vinScanning, setVinScanning] = useState(false);
  const [plateScanning, setPlateScanning] = useState(false); // eslint-disable-line no-unused-vars
  const [vinDecoding, setVinDecoding] = useState(false);

  // Kenneth AI chat
  const [chatMessages, setChatMessages] = useState([]);
  const chatMessagesRef = useRef([]);
  const [userInput, setUserInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatDone, setChatDone] = useState(false);
  const [showVinScanInChat, setShowVinScanInChat] = useState(false);
  const [showPlateScanInChat, setShowPlateScanInChat] = useState(false); // eslint-disable-line no-unused-vars
  const [showVehicleForm, setShowVehicleForm] = useState(false);
  const [vehicleConfirmed, setVehicleConfirmed] = useState(false);
  const [nhtsaModels, setNhtsaModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [kennethSummary, setKennethSummary] = useState('');
  const kennethSummaryPromiseRef = useRef(null);
  const chatSessionRef = useRef(null);
  const chatEndRef = useRef(null);
  const sigRef = useRef(null);
  const [signature, setSignature] = useState(null);
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);
  const [disclaimerRead, setDisclaimerRead] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const makes = MAKES; // alias for hook closures
  
          // Handle Pre-fill from URL
  
          useEffect(() => {
  
            const params = new URLSearchParams(window.location.search);
  
            console.log('IntakeForm: Parsing URL parameters...', window.location.search);
  
            const updates = {};
  
            
  
            if (params.has('fn')) updates.firstName = params.get('fn');
  
            if (params.has('ln')) updates.lastName = params.get('ln');
  
            if (params.has('ph')) updates.phone = formatPhone(params.get('ph'));
  
            if (params.has('em')) updates.email = params.get('em');
  
            
  
            // Address pre-fill
  
            if (params.has('sa')) updates.streetAddress = params.get('sa');
  
            if (params.has('ct')) updates.city = params.get('ct');
  
            if (params.has('st')) updates.state = params.get('st').toUpperCase();
  
            if (params.has('zp')) updates.zipCode = params.get('zp');
  
        
  
            // Vehicle pre-fill
  
            if (params.has('vy')) {
  
              const year = parseInt(params.get('vy'));
  
              if (!isNaN(year)) updates.vehicleYear = year;
  
            }
  
            if (params.has('vm')) updates.vehicleMake = params.get('vm');
  
            if (params.has('vmod')) updates.vehicleModel = params.get('vmod');
  
        
  
            // Service pre-fill
  
            if (params.has('rv')) updates.reasonForVisit = params.get('rv');
  
            if (params.has('desc')) updates.additionalNotes = params.get('desc');
  
        
  
            if (Object.keys(updates).length > 0) {
  
              console.log('IntakeForm: Applying pre-fill updates:', updates);
  
              setForm(prev => ({ ...prev, ...updates }));
  
            }
  
          }, []);

  // --- Kenneth AI chat helpers ---

  const addChatMessage = useCallback((msg) => {
    chatMessagesRef.current = [...chatMessagesRef.current, msg];
    setChatMessages([...chatMessagesRef.current]);
  }, []);

  const fetchNhtsaModels = useCallback(async (make, year) => {
    if (!make || !year) { setNhtsaModels([]); return; }
    setLoadingModels(true);
    try {
      const res = await fetch(`https://vpic.nhtsa.dot.gov/api/vehicles/GetModelsForMakeYear/make/${encodeURIComponent(make)}/modelyear/${year}?format=json`);
      const data = await res.json();
      const models = (data.Results || []).map(r => r.Model_Name).filter(Boolean).sort();
      setNhtsaModels(models);
    } catch {
      setNhtsaModels([]);
    } finally {
      setLoadingModels(false);
    }
  }, []);

  const parseKenneth = useCallback((text) => {
    const requestVinScan = text.includes('[SCAN_VIN]');
    const requestPlateScan = text.includes('[SCAN_PLATE]');
    const requestVehicleForm = text.includes('[VEHICLE_FORM]');
    const conversationDone = text.includes('[DONE]');
    const display = text
      .replace(/\[SCAN_VIN\]/g, '')
      .replace(/\[SCAN_PLATE\]/g, '')
      .replace(/\[VEHICLE_FORM\]/g, '')
      .replace(/\[DONE\]/g, '')
      .trim();
    return { display, requestVinScan, requestPlateScan, requestVehicleForm, conversationDone };
  }, []);

  const handleChatDone = useCallback((transcript) => {
    setChatDone(true);
    // Store the in-flight promise so handleSubmit can await it if needed
    const promise = generateTechSummary(transcript)
      .then(summary => { setKennethSummary(summary); return summary; })
      .catch(err => { console.error(err); return ''; });
    kennethSummaryPromiseRef.current = promise;
  }, []);

  // Initialize Kenneth when step 3 is reached
  useEffect(() => {
    if (step !== 3 || chatSessionRef.current) return;
    let cancelled = false;
    (async () => {
      setChatLoading(true);
      try {
        chatSessionRef.current = { history: [] };
        // Hidden trigger — sends customer name so Kenneth uses it correctly
        const opener = `begin. The customer's first name is ${form.firstName.trim()}.`;
        const raw = await chatTurn([], opener, advisorName);
        if (cancelled) return;
        chatSessionRef.current.history = [{ role: 'user', text: opener }, { role: 'model', text: raw }];
        const { display, requestVinScan, requestPlateScan, requestVehicleForm, conversationDone } = parseKenneth(raw);
        const msg = { role: 'model', text: display };
        chatMessagesRef.current = [msg];
        setChatMessages([msg]);
        if (requestVinScan) setShowVinScanInChat(true);
        if (requestPlateScan) setShowPlateScanInChat(true);
        if (requestVehicleForm) setShowVehicleForm(true);
        if (conversationDone) handleChatDone([msg]);
      } catch (err) {
        console.error('Kenneth init error:', err);
      } finally {
        if (!cancelled) setChatLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [step, parseKenneth, handleChatDone]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, chatLoading]);

  // `modelText` (optional) is what Kenneth receives; `text` is what the customer sees in the bubble.
  // Used for automated events (form submit, scans) so the model knows they came from the UI, not typing.
  const sendChatMessage = useCallback(async (text, modelText) => {
    if (!text.trim() || chatLoading || !chatSessionRef.current || chatDone) return;
    const toModel = (modelText || text).trim();
    const userMsg = { role: 'user', text: text.trim() };
    addChatMessage(userMsg);
    setUserInput('');
    setChatLoading(true);
    try {
      const h = chatSessionRef.current.history;
      const raw = await chatTurn(h, toModel, advisorName);
      chatSessionRef.current.history = [...h, { role: 'user', text: toModel }, { role: 'model', text: raw }];
      const { display, requestVinScan, requestPlateScan, requestVehicleForm, conversationDone } = parseKenneth(raw);
      const kennethMsg = { role: 'model', text: display };
      addChatMessage(kennethMsg);
      if (requestVinScan) setShowVinScanInChat(true);
      if (requestPlateScan) setShowPlateScanInChat(true);
      if (requestVehicleForm) setShowVehicleForm(true);
      if (conversationDone) handleChatDone(chatMessagesRef.current);
    } catch (err) {
      console.error('Kenneth chat error:', err);
      addChatMessage({ role: 'model', text: "Sorry, I'm having a little trouble. Could you repeat that?" });
    } finally {
      setChatLoading(false);
    }
  }, [chatLoading, chatDone, addChatMessage, parseKenneth, handleChatDone, advisorName]);

  const handleVehicleFormSubmit = useCallback(() => {
    if (!form.vehicleYear || !form.vehicleMake || !form.vehicleModel) return;
    setVehicleConfirmed(true);
    setShowVehicleForm(false);
    setShowVinScanInChat(false);
    const engineStr = form.vehicleEngine.trim() ? `. Engine: ${form.vehicleEngine.trim()}` : '';
    const vehicleLine = `${form.vehicleYear} ${form.vehicleMake} ${form.vehicleModel}${engineStr}`;
    sendChatMessage(
      `My vehicle is a ${vehicleLine}.`,
      `[VEHICLE FORM SUBMITTED — vehicle is now confirmed: ${vehicleLine}. Do not ask for the form or a scan again; continue with the concern.]`
    );
  }, [form, sendChatMessage]);

  // Scan a VIN or plate from within the Kenneth chat
  const handleChatScan = useCallback(async (file, type) => {
    if (!file) return;
    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      if (type === 'plate') { setPlatePhoto(file); setPlatePreview(reader.result); }
      else { setVinPhoto(file); setVinPreview(reader.result); }
    };
    reader.readAsDataURL(file);

    if (type === 'plate') setPlateScanning(true);
    else setVinScanning(true);

    try {
      const { value, vehicleData } = await scanImage(file, type);
      if (type === 'plate') {
        setPlateScanning(false);
        if (value) {
          setForm(p => ({ ...p, licensePlate: value.toUpperCase() }));
          setShowPlateScanInChat(false);
          sendChatMessage(`I scanned my plate — it's ${value.toUpperCase()}.`, `[PLATE SCAN SUCCESSFUL: ${value.toUpperCase()}]`);
        }
      } else {
        setVinScanning(false);
        if (value && vehicleData) {
          // Best case: VIN extracted AND decoded — vehicle confirmed
          setForm(p => {
            const matchedMake = makes.find(m => m.toLowerCase() === vehicleData.make.toLowerCase()) || p.vehicleMake;
            return { ...p, vin: value.toUpperCase(), vehicleYear: vehicleData.year || p.vehicleYear, vehicleMake: matchedMake, vehicleModel: vehicleData.model || p.vehicleModel };
          });
          setVehicleConfirmed(true);
          setShowVinScanInChat(false);
          setShowVehicleForm(false);
          sendChatMessage(
            `I scanned my VIN — it's ${value.toUpperCase()}. It decoded as a ${vehicleData.year} ${vehicleData.make} ${vehicleData.model}.`,
            `[VIN SCAN SUCCESSFUL — vehicle is now confirmed: VIN ${value.toUpperCase()}, ${vehicleData.year} ${vehicleData.make} ${vehicleData.model}. Do not ask for the form or a scan again; continue with the concern.]`
          );
        } else if (value && !vehicleData) {
          // VIN extracted but NHTSA decode failed — keep scan button, open form as fallback
          setForm(p => ({ ...p, vin: value.toUpperCase() }));
          setShowVehicleForm(true);
          sendChatMessage(`I scanned my VIN — it's ${value.toUpperCase()}, but I wasn't able to decode it. I'll enter my vehicle info below.`);
        } else {
          // Scan couldn't read a VIN at all — keep button available, open form as fallback
          setShowVehicleForm(true);
          sendChatMessage("I couldn't get a clear scan of the VIN sticker. I'll enter my vehicle info manually.");
        }
      }
    } catch (err) {
      console.error('Chat scan error:', err);
      setVinScanning(false);
      setPlateScanning(false);
      // On any scan error, surface the vehicle form so the customer isn't stuck
      setShowVehicleForm(true);
    }
  }, [sendChatMessage, makes]);

  // Auto-decode VIN via NHTSA when the typed VIN reaches 17 characters
  useEffect(() => {
    const vin = form.vin.replace(/\s/g, '');
    if (vin.length !== 17 || vinScanning || vinDecoding) return;
    let cancelled = false;
    setVinDecoding(true);
    decodeVin(vin).then((vehicleData) => {
      if (cancelled) return;
      if (vehicleData) {
        setForm(p => {
          const matchedMake = makes.find(
            m => m.toLowerCase() === vehicleData.make.toLowerCase()
          ) || p.vehicleMake;
          return {
            ...p,
            vehicleYear: vehicleData.year || p.vehicleYear,
            vehicleMake: matchedMake,
            vehicleModel: vehicleData.model || p.vehicleModel,
          };
        });
      }
    }).catch(console.error).finally(() => { if (!cancelled) setVinDecoding(false); });
    return () => { cancelled = true; };
  }, [form.vin]); // eslint-disable-line react-hooks/exhaustive-deps

          const states = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'];
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(p => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  const handlePhone = (e) => {
    setForm(p => ({ ...p, phone: formatPhone(e.target.value) }));
  };

  const clearSig = () => {
    if (sigRef.current) sigRef.current.clear();
    setSignature(null);
  };

  const saveSig = () => {
    if (sigRef.current && !sigRef.current.isEmpty()) {
      setSignature(sigRef.current.toDataURL('image/png'));
    }
  };

  const handleScroll = useCallback((e) => {
    if (e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 80) {
      setDisclaimerRead(true);
    }
  }, []);

  const valid = (s) => {
    switch (s) {
      case 1: return form.firstName && form.lastName && form.phone.length >= 14 && form.email.includes('@');
      case 2: return form.streetAddress && form.city && form.state && form.zipCode;
      case 3: return chatDone && vehicleConfirmed;
      case 4: return disclaimerRead && signature;
      default: return false;
    }
  };

  const upload = async (file, path) => {
    const r = ref(storage, path);
    await uploadBytes(r, file);
    return getDownloadURL(r);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!signature || !disclaimerRead) {
      setError('Please complete all required fields');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const ip = await getIPAddress();
      const device = getDeviceInfo();
      const ts = new Date();
      const txId = generateTransactionId();
      let plateUrl = platePhoto ? await upload(platePhoto, 'intakes/' + shopId + '/' + txId + '/plate.jpg') : null;
      let vinUrl = vinPhoto ? await upload(vinPhoto, 'intakes/' + shopId + '/' + txId + '/vin.jpg') : null;
      const sigBlob = await (await fetch(signature)).blob();
      const sigUrl = await upload(sigBlob, 'intakes/' + shopId + '/' + txId + '/signature.png');
      // Await the tech summary promise so fast submitters still get it
      let resolvedSummary = kennethSummary;
      if (!resolvedSummary && kennethSummaryPromiseRef.current) {
        resolvedSummary = await kennethSummaryPromiseRef.current;
      }

      const intakeDoc = {
        transactionId: txId,
        shopId: shopId,
        status: 'pending',
        createdAt: serverTimestamp(),
        customer: {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          fullName: form.firstName.trim() + ' ' + form.lastName.trim(),
          phone: form.phone,
          phoneDigits: form.phone.replace(/\D/g, ''),
          email: form.email.trim().toLowerCase()
        },
        address: {
          street: form.streetAddress.trim(),
          city: form.city.trim(),
          state: form.state,
          zipCode: form.zipCode.trim(),
          full: form.streetAddress.trim() + ', ' + form.city.trim() + ', ' + form.state + ' ' + form.zipCode.trim()
        },
        vehicle: {
          year: form.vehicleYear,
          make: form.vehicleMake,
          model: form.vehicleModel.trim(),
          engine: form.vehicleEngine.trim() || null,
          fullDescription: [form.vehicleYear, form.vehicleMake, form.vehicleModel.trim(), form.vehicleEngine.trim()].filter(Boolean).join(' '),
          licensePlate: form.licensePlate.trim().toUpperCase(),
          vin: form.vin.trim().toUpperCase()
        },
        photos: { licensePlate: plateUrl, vin: vinUrl },
        service: {
          advisorName,
          transcript: chatMessagesRef.current.map(m => ({ role: m.role, text: m.text })),
          techSummary: resolvedSummary || null,
        },
        legalCompliance: {
          signatureUrl: sigUrl,
          signatureTimestampUTC: ts.toISOString(),
          disclaimerVersion: '4.0-CUSTODY-' + (shop?.address?.state || 'GENERIC'),
          tcpaConsentGiven: true,
          mechanicsLienAcknowledged: true,
          esignActCompliance: true,
          uetaCompliance: true
        },
        auditTrail: {
          transactionId: txId,
          timestampUTC: ts.toISOString(),
          timestampUnix: ts.getTime(),
          ipAddress: ip,
          userAgent: device.userAgent,
          browser: device.browser,
          operatingSystem: device.os,
          deviceSimplified: device.simplified,
          screenResolution: device.screenResolution,
          timezone: device.timezone
        }
      };
      await addDoc(collection(db, 'intakes'), intakeDoc);
      try {
        const fn = getFunctions();
        await httpsCallable(fn, 'syncCustomerToSquare')({
          shopId: shopId,
          customer: {
            givenName: form.firstName.trim(),
            familyName: form.lastName.trim(),
            phoneNumber: form.phone,
            emailAddress: form.email.trim().toLowerCase()
          },
          vehicle: {
            vin: form.vin.trim().toUpperCase(),
            licensePlate: form.licensePlate.trim().toUpperCase(),
            year: form.vehicleYear,
            make: form.vehicleMake,
            model: form.vehicleModel.trim()
          }
        });
      } catch (sqErr) {
        console.warn('Square sync:', sqErr);
      }
      setResult({ transactionId: txId, vehicle: intakeDoc.vehicle.fullDescription, timestamp: ts.toISOString() });
      setSuccess(true);
    } catch (err) {
      console.error(err);
      setError('Failed: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (shopLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Shop not found
  if (shopError || !shop) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">Shop Not Found</h2>
          <p className="text-slate-600">This link appears to be invalid. Please contact the shop for the correct check-in link.</p>
        </div>
      </div>
    );
  }

  // Shop suspended or inactive - GRACEFUL FALLBACK
  if (shop.status === 'suspended' || shop.status === 'inactive' || shop.status === 'pending' || shop.subscriptionStatus === 'past_due' || shop.subscriptionStatus === 'canceled') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 text-center max-w-md">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">System Temporarily Unavailable</h2>
          <p className="text-slate-600 mb-6">Our online check-in system is currently down for maintenance. Please contact the shop directly to check in your vehicle.</p>
          {shop.ownerPhone && (
            <a 
              href={'tel:' + shop.ownerPhone.replace(/\D/g, '')} 
              className="inline-block px-6 py-3 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-600 transition-colors"
            >
              Call {shop.shopName}
            </a>
          )}
          {!shop.ownerPhone && shop.ownerEmail && (
            <a 
              href={'mailto:' + shop.ownerEmail} 
              className="inline-block px-6 py-3 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-600 transition-colors"
            >
              Email {shop.shopName}
            </a>
          )}
          {shop.shopName && !shop.ownerPhone && !shop.ownerEmail && (
            <p className="text-slate-500 text-sm">Please contact <strong>{shop.shopName}</strong> directly.</p>
          )}
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold mb-2">Check-In Complete!</h2>
          <p className="text-slate-600 mb-4">{shop.shopName} has been notified.</p>
          <div className="bg-slate-50 rounded-lg p-4 text-sm">
            <p><span className="text-slate-500">Confirmation:</span> <span className="font-mono font-bold">{result && result.transactionId}</span></p>
            <p className="mt-2"><span className="text-slate-500">Vehicle:</span> {result && result.vehicle}</p>
          </div>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <header className="bg-slate-900/95 border-b border-slate-700 sticky top-0 z-50 px-4 py-4">
        <div className="max-w-2xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold text-white">
              <span className="text-amber-400">*</span> {shop.shopName}
            </h1>
            <p className="text-xs text-slate-400">Digital Check-In</p>
          </div>
          <div className="flex space-x-1">
            {[1, 2, 3, 4].map(s => (
              <div key={s} className={'w-8 h-1.5 rounded-full ' + (s < step ? 'bg-emerald-500' : s === step ? 'bg-amber-400' : 'bg-slate-700')}></div>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 pb-32">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Step 1: Customer Info */}
          {step >= 1 && (
            <section className={'bg-white rounded-2xl shadow-xl overflow-hidden ' + (step === 1 ? 'ring-2 ring-amber-400' : '')}>
              <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-6 py-4 flex items-center">
                <span className={'w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm mr-3 ' + (step > 1 ? 'bg-emerald-500 text-white' : 'bg-amber-400 text-slate-900')}>{step > 1 ? 'Y' : '1'}</span>
                <h2 className="text-lg font-semibold text-white">Your Information</h2>
              </div>
              {step === 1 && (
                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">First Name *</label>
                      <input type="text" name="firstName" value={form.firstName} onChange={handleChange} required className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-400" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Last Name *</label>
                      <input type="text" name="lastName" value={form.lastName} onChange={handleChange} required className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-400" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Phone *</label>
                    <input type="tel" name="phone" value={form.phone} onChange={handlePhone} required className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-400" placeholder="(555) 123-4567" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                    <input type="email" name="email" value={form.email} onChange={handleChange} required className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-400" />
                  </div>
                  <button type="button" onClick={() => valid(1) && setStep(2)} disabled={!valid(1)} className="w-full py-3 bg-amber-400 text-slate-900 font-semibold rounded-lg hover:bg-amber-500 disabled:opacity-50">Continue</button>
                </div>
              )}
            </section>
          )}

          {/* Step 2: Address */}
          {step >= 2 && (
            <section className={'bg-white rounded-2xl shadow-xl overflow-hidden ' + (step === 2 ? 'ring-2 ring-amber-400' : '')}>
              <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-6 py-4 flex items-center">
                <span className={'w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm mr-3 ' + (step > 2 ? 'bg-emerald-500 text-white' : 'bg-amber-400 text-slate-900')}>{step > 2 ? 'Y' : '2'}</span>
                <h2 className="text-lg font-semibold text-white">Your Address</h2>
              </div>
              {step === 2 && (
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Street Address *</label>
                    <input type="text" name="streetAddress" value={form.streetAddress} onChange={handleChange} required className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-400" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">City *</label>
                      <input type="text" name="city" value={form.city} onChange={handleChange} required className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-400" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">State *</label>
                      <select name="state" value={form.state} onChange={handleChange} required className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-400 bg-white">
                        <option value="">Select</option>
                        {states.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">ZIP Code *</label>
                    <input type="text" name="zipCode" value={form.zipCode} onChange={handleChange} required className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-400" />
                  </div>
                  <div className="flex space-x-3">
                    <button type="button" onClick={() => setStep(1)} className="flex-1 py-3 bg-slate-200 text-slate-700 font-semibold rounded-lg">Back</button>
                    <button type="button" onClick={() => valid(2) && setStep(3)} disabled={!valid(2)} className="flex-1 py-3 bg-amber-400 text-slate-900 font-semibold rounded-lg hover:bg-amber-500 disabled:opacity-50">Continue</button>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Step 3: Kenneth AI Chat */}
          {step >= 3 && (
            <section className={'bg-white rounded-2xl shadow-xl overflow-hidden ' + (step === 3 ? 'ring-2 ring-amber-400' : '')}>
              <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-6 py-4 flex items-center gap-3">
                <span className={'w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm shrink-0 ' + (step > 3 ? 'bg-emerald-500 text-white' : 'bg-amber-400 text-slate-900')}>{step > 3 ? '✓' : '3'}</span>
                <div>
                  <h2 className="text-lg font-semibold text-white leading-tight">Vehicle & Service Info</h2>
                  <p className="text-xs text-slate-400">{advisorName}, our AI service advisor, will assist you</p>
                </div>
              </div>
              {step === 3 && (
                <div className="flex flex-col" style={{ height: '520px' }}>
                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
                    {chatMessages.length === 0 && chatLoading && (
                      <div className="flex items-end gap-2">
                        <div className="w-8 h-8 rounded-full bg-amber-400 flex items-center justify-center text-slate-900 font-bold text-sm shrink-0">{advisorName[0].toUpperCase()}</div>
                        <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-4 py-3 flex gap-1 shadow-sm">
                          <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    )}
                    {chatMessages.map((msg, i) => (
                      <div key={i} className={'flex gap-2 items-end ' + (msg.role === 'user' ? 'flex-row-reverse' : 'flex-row')}>
                        {msg.role === 'model' && (
                          <div className="w-8 h-8 rounded-full bg-amber-400 flex items-center justify-center text-slate-900 font-bold text-sm shrink-0">{advisorName[0].toUpperCase()}</div>
                        )}
                        <div className={'max-w-[78%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed shadow-sm ' + (msg.role === 'model' ? 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm' : 'bg-amber-400 text-slate-900 rounded-br-sm font-medium')}>
                          {msg.text}
                        </div>
                      </div>
                    ))}
                    {chatMessages.length > 0 && chatLoading && (
                      <div className="flex items-end gap-2">
                        <div className="w-8 h-8 rounded-full bg-amber-400 flex items-center justify-center text-slate-900 font-bold text-sm shrink-0">{advisorName[0].toUpperCase()}</div>
                        <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-4 py-3 flex gap-1 shadow-sm">
                          <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    )}

                    {/* VIN scan button — appears when Kenneth requests it */}
                    {showVinScanInChat && !vehicleConfirmed && !chatDone && (
                      <div className="flex flex-wrap gap-2 justify-center py-1">
                        <label className={'flex items-center gap-2 px-5 py-2.5 rounded-xl cursor-pointer text-sm font-semibold transition-colors border ' + (vinScanning ? 'bg-amber-50 border-amber-400 text-amber-700 pointer-events-none' : 'bg-slate-900 border-slate-700 text-white hover:bg-slate-800')}>
                          <input type="file" accept="image/*" capture="environment" className="hidden" disabled={vinScanning} onChange={e => e.target.files[0] && handleChatScan(e.target.files[0], 'vin')} />
                          {vinScanning ? '⏳ Scanning VIN...' : '📷 Scan VIN Sticker'}
                        </label>
                        {!showVehicleForm && (
                          <button type="button" onClick={() => setShowVehicleForm(true)} className="px-5 py-2.5 rounded-xl text-sm font-semibold border border-slate-300 text-slate-600 bg-white hover:bg-slate-50">
                            Can't scan? Enter manually
                          </button>
                        )}
                      </div>
                    )}

                    {/* Inline vehicle form — NHTSA-powered dropdowns */}
                    {showVehicleForm && !vehicleConfirmed && !chatDone && (
                      <div className="bg-white border border-amber-200 rounded-2xl p-4 mx-1 shadow-sm">
                        <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-3">Vehicle Info</p>
                        <div className="space-y-2">
                          <select
                            value={form.vehicleYear}
                            onChange={e => { setForm(p => ({ ...p, vehicleYear: e.target.value, vehicleMake: '', vehicleModel: '' })); setNhtsaModels([]); }}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 bg-white"
                          >
                            <option value="">Year…</option>
                            {Array.from({ length: 36 }, (_, i) => new Date().getFullYear() - i).map(y => (
                              <option key={y} value={y}>{y}</option>
                            ))}
                          </select>
                          <select
                            value={form.vehicleMake}
                            onChange={e => { setForm(p => ({ ...p, vehicleMake: e.target.value, vehicleModel: '' })); fetchNhtsaModels(e.target.value, form.vehicleYear); }}
                            disabled={!form.vehicleYear}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 bg-white disabled:opacity-50"
                          >
                            <option value="">Make…</option>
                            {makes.map(m => <option key={m} value={m}>{m}</option>)}
                          </select>
                          <select
                            value={form.vehicleModel}
                            onChange={e => setForm(p => ({ ...p, vehicleModel: e.target.value }))}
                            disabled={!form.vehicleMake || loadingModels}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 bg-white disabled:opacity-50"
                          >
                            <option value="">{loadingModels ? 'Loading models…' : 'Model…'}</option>
                            {nhtsaModels.map(m => <option key={m} value={m}>{m}</option>)}
                          </select>
                          <input
                            type="text"
                            placeholder="Engine (optional — e.g. 2.0L 4 cyl)"
                            value={form.vehicleEngine}
                            onChange={e => setForm(p => ({ ...p, vehicleEngine: e.target.value }))}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400"
                          />
                          <button
                            type="button"
                            onClick={handleVehicleFormSubmit}
                            disabled={!form.vehicleYear || !form.vehicleMake || !form.vehicleModel}
                            className="w-full py-2.5 bg-amber-400 text-slate-900 font-semibold rounded-lg hover:bg-amber-500 disabled:opacity-40 text-sm"
                          >
                            That's my vehicle
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Photo previews */}
                    {(vinPreview || platePreview) && (
                      <div className="flex gap-2 flex-wrap justify-center pt-1">
                        {vinPreview && <img src={vinPreview} alt="VIN" className="h-14 rounded-lg border border-slate-200 shadow-sm" />}
                        {platePreview && <img src={platePreview} alt="Plate" className="h-14 rounded-lg border border-slate-200 shadow-sm" />}
                      </div>
                    )}

                    <div ref={chatEndRef} />
                  </div>

                  {/* Input / Done area */}
                  {!chatDone ? (
                    <div className="border-t border-slate-200 p-3 flex gap-2 bg-white">
                      <input
                        type="text"
                        value={userInput}
                        onChange={e => setUserInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChatMessage(userInput); } }}
                        placeholder="Type your message…"
                        disabled={chatLoading}
                        className="flex-1 px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:bg-slate-50"
                      />
                      <button
                        type="button"
                        onClick={() => sendChatMessage(userInput)}
                        disabled={chatLoading || !userInput.trim()}
                        className="px-4 py-2.5 bg-amber-400 text-slate-900 font-semibold rounded-xl hover:bg-amber-500 disabled:opacity-40 text-sm shrink-0"
                      >Send</button>
                    </div>
                  ) : (
                    <div className="border-t border-slate-200 p-4 space-y-3 bg-white">
                      <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                        <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                        <span className="text-sm font-medium">All set! Your information has been captured.</span>
                      </div>
                      <div className="flex gap-3">
                        <button type="button" onClick={() => setStep(2)} className="flex-1 py-3 bg-slate-200 text-slate-700 font-semibold rounded-lg">Back</button>
                        <button type="button" onClick={() => setStep(4)} className="flex-1 py-3 bg-amber-400 text-slate-900 font-semibold rounded-lg hover:bg-amber-500">Review & Sign</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          {/* Step 4: Signature */}
          {step >= 4 && (
            <section className={'bg-white rounded-2xl shadow-xl overflow-hidden ' + (step === 4 ? 'ring-2 ring-red-400' : '')}>
              <div className="bg-gradient-to-r from-red-800 to-red-700 px-6 py-4 flex items-center">
                <span className="w-8 h-8 rounded-full bg-white text-red-800 flex items-center justify-center font-bold text-sm mr-3">4</span>
                <h2 className="text-lg font-semibold text-white">Chain of Custody & Authorization</h2>
              </div>
              {step === 4 && (
                <div className="p-6 space-y-5">
                  <div className="border border-slate-200 rounded-xl overflow-hidden">
                    <button type="button" onClick={() => setDisclaimerOpen(!disclaimerOpen)} className="w-full px-4 py-3 bg-slate-50 flex items-center justify-between hover:bg-slate-100">
                      <span className="font-medium text-slate-700">
                        Chain of Custody Agreement
                        {!disclaimerRead && <span className="text-xs text-red-600 ml-2">(Required - tap to read)</span>}
                        {disclaimerRead && <span className="text-xs text-emerald-600 ml-2">Read</span>}
                      </span>
                      <span>{disclaimerOpen ? '-' : '+'}</span>
                    </button>
                    {disclaimerOpen && (
                      <div onScroll={handleScroll} className="max-h-64 overflow-y-auto p-4 bg-white border-t text-xs text-slate-700 leading-relaxed">
                        <pre className="whitespace-pre-wrap font-sans">{getDisclaimer(shop)}</pre>
                        {!disclaimerRead && (
                          <div className="sticky bottom-0 bg-gradient-to-t from-amber-100 to-transparent pt-8 pb-2 text-center">
                            <span className="inline-block px-4 py-2 bg-amber-400 text-slate-900 rounded-full text-sm font-bold animate-bounce">
                              SCROLL DOWN TO CONTINUE
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Your Signature *
                      {signature && <span className="text-emerald-600 text-xs ml-2">Captured</span>}
                    </label>
                    <div className="border-2 border-dashed border-slate-300 rounded-xl overflow-hidden bg-slate-50">
                      <SignatureCanvas
                        ref={sigRef}
                        canvasProps={{
                          className: 'w-full h-36',
                          style: { width: '100%', height: '144px', backgroundColor: '#f8fafc', touchAction: 'none' }
                        }}
                        onEnd={saveSig}
                      />
                    </div>
                    <div className="flex justify-between mt-2">
                      <p className="text-xs text-slate-500">Sign with finger or stylus</p>
                      <button type="button" onClick={clearSig} className="text-sm text-red-600 font-medium">Clear</button>
                    </div>
                  </div>
                  {error && <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">{error}</div>}
                  <div className="flex space-x-3">
                    <button type="button" onClick={() => setStep(3)} className="flex-1 py-3 bg-slate-200 text-slate-700 font-semibold rounded-lg">Back</button>
                    <button type="submit" disabled={submitting || !signature || !disclaimerRead} className="flex-1 py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center">
                      {submitting ? 'Submitting...' : 'Submit Authorization'}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 text-center">Your electronic signature is legally binding under the ESIGN Act and UETA.</p>
                </div>
              )}
            </section>
          )}
        </form>
      </main>

      <div className="fixed bottom-0 left-0 right-0 bg-slate-900/95 border-t border-slate-700 p-4">
        <div className="max-w-2xl mx-auto flex justify-between items-center">
          <span className="text-sm text-slate-400">Step {step} of 4</span>
          <span className="text-xs text-slate-300">Encrypted</span>
        </div>
      </div>
    </div>
  );
};

export default IntakeForm;
